import "./styles.sass";
import uiAssets from "../../assets";
import imgContentStudioBanner from "../../assets/images/Selected photo.jpg";
import { ROUTE_AI_GENERATE } from "../../route";
import { MarkdownWithMath } from "../../components/latex-renderer";
import useHandler from "./handler";
import React, { useEffect, useState, useRef } from "react";

const CustomSelect = ({
  value,
  onChange,
  options,
  defaultLabel,
  isObjectOption,
  showDefaultOption = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  let selectedLabel = defaultLabel;
  if (value) {
    if (isObjectOption) {
      selectedLabel =
        options.find((o) => o.key === value)?.value || defaultLabel;
    } else {
      selectedLabel = value;
    }
  }

  return (
    <div
      className="desc_subject_wrapper"
      style={{ position: "relative", minWidth: "180px" }}
      ref={dropdownRef}
    >
      <div
        className="desc_drop_down_wrapper"
        onClick={() => setIsOpen(!isOpen)}
      >
        <p>{selectedLabel}</p>
        <img
          src={uiAssets.ic_chevron_down}
          alt="arrow"
          style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </div>
      {isOpen && (
        <div className="desc_options_list">
          {showDefaultOption && (
            <div
              className={`desc_option_item ${value === "" ? "selected" : ""}`}
              onClick={() => {
                onChange("");
                setIsOpen(false);
              }}
            >
              {defaultLabel}
            </div>
          )}
          {options?.map((opt, i) => {
            const optValue = isObjectOption ? opt.key : opt;
            const optLabel = isObjectOption ? opt.value : opt;
            return (
              <div
                key={i}
                className={`desc_option_item ${value === optValue ? "selected" : ""}`}
                onClick={() => {
                  onChange(optValue);
                  setIsOpen(false);
                }}
              >
                {optLabel}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const MediaPopup = ({
  data,
  onClose,
  audioRef,
  videoRef,
  showAskBox,
  setShowAskBox,
  question,
  setQuestion,
  inputRef,
  audioRes,
  videoRes,
  aiAudioChat,
  aiVideoChat,
  isListening,
  handleMicClick,
}) => {
  const { content, topic, type, url, filters } = data || {};

  useEffect(() => {
    if (showAskBox && inputRef?.current) {
      inputRef.current.focus();
    }
  }, [showAskBox]);

  const handleAsk = async () => {
    if (!question.trim()) return;

    try {
      if (type === "audio") {
        await aiAudioChat(filters);
      } else if (type === "video") {
        await aiVideoChat(filters);
      }

      setQuestion("");

      // ✅ Close Ask Popup AFTER response
      // setShowAskBox(false);
    } catch (e) {
      console.error("Ask failed:", e);
    }
  };

  return (
    <section className="audio-overlay">
      <section className="audio-popup">
        <div className="popup-content">
          <button onClick={onClose} className="close-button">
            ✖
          </button>

          <h4 className="topic">{topic}</h4>

          {/* 🎧 AUDIO */}
          {type === "audio" && (
            <audio
              onContextMenu={(e) => e.preventDefault()}
              controlsList="nodownload"
              ref={audioRef}
              controls
              className="controls_audio"
              onPause={() => setShowAskBox(true)}
            >
              <source src={url} type="audio/mpeg" />
            </audio>
          )}

          {/* 🎬 VIDEO */}
          {type === "video" && (
            <video
              onContextMenu={(e) => e.preventDefault()}
              controlsList="nodownload"
              ref={videoRef}
              controls
              className="video-player"
              width="100%"
              onPause={() => setShowAskBox(true)}
            >
              <source src={url} type="video/mp4" />
            </video>
          )}

          {showAskBox && (
            <div className="ask-popup">
              <div className="ask-header">
                <h3>Ask Questions</h3>
                <button
                  onClick={() => setShowAskBox(false)}
                  className="ask-close-btn"
                >
                  <img src={uiAssets.ic_close_white} alt="close" />
                </button>
              </div>

              <div className="ask-body">
                <section className="ask_messages_container">
                  <div className="assistant-bubble">
                    <p>
                      Hi! I'm your learning assistant. Ask me anything about{" "}
                      {topic}!
                    </p>
                  </div>
                  {audioRes?.answer && (
                    <div className="assistant-bubble response">
                      <MarkdownWithMath text={audioRes.answer} />
                    </div>
                  )}
                  {videoRes?.answer && (
                    <div className="assistant-bubble response">
                      <MarkdownWithMath text={videoRes.answer} />
                    </div>
                  )}
                </section>

                <div className="ask-input-footer">
                  <div className="ask-input-wrapper">
                    <input
                      ref={inputRef}
                      type="text"
                      placeholder="Ask about the video..."
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleAsk();
                        }
                      }}
                    />
                    <div className="ask-actions">
                      <img
                        src={uiAssets.ic_mic}
                        alt="mic"
                        className={`ask-mic-icon ${isListening ? "listening" : ""}`}
                        onClick={handleMicClick}
                      />
                      <button
                        className="ask-send-btn"
                        onClick={handleAsk}
                        disabled={!question.trim()}
                      >
                        <img src={uiAssets.ic_sign_arrow} alt="send" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </section>
  );
};

export const LearningAssets = () => {
  const {
    STATUS,
    LANGUAGES,
    filters,
    apiAssets,
    question,
    showAskBox,
    popupMedia,
    audioRef,
    videoRef,
    audioRes,
    videoRes,
    inputRef,
    subjects,
    chapters,
    mediums,
    role,
    setSelectedSubject,
    setPopupMedia,
    setShowAskBox,
    setQuestion,
    setFilters,
    setSelectedMedium,
    navigate,
    aiAudioChat,
    aiVideoChat,
    toggleHideAsset,
  } = useHandler();

  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.lang = "en-IN";
    recognition.interimResults = false;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setQuestion(transcript);
    };
    recognition.onerror = () => setIsListening(false);

    recognitionRef.current = recognition;
  }, [setQuestion]);

  const handleMicClick = () => {
    const recognition = recognitionRef.current;
    if (!recognition) return;
    if (isListening) {
      recognition.stop();
    } else {
      try {
        recognition.start();
      } catch (e) {
        console.log("Already started");
      }
    }
  };

  const groupAssets = (assets) => {
    return assets.reduce((acc, asset) => {
      const { subject } = asset;
      const sKey = subject && subject !== "Other" ? subject : "__root__";
      if (!acc[sKey]) acc[sKey] = [];
      acc[sKey].push(asset);
      return acc;
    }, {});
  };

  const groupedData = groupAssets(apiAssets || []);
  const [activeTopic, setActiveTopic] = useState(null);

  const AssetCard = ({ data }) => {
    const { topic, display_name, type, hidden, asset_id, url } = data;

    return (
      <section className={`la-item-card ${hidden ? "is-hidden" : ""}`}>
        <div className="la-thumb-container" onClick={() => setPopupMedia(data)}>
          {type === "video" ? (
            <video
              src={url}
              muted
              preload="metadata"
              className="la-thumb-img"
            />
          ) : (
            <img src={uiAssets.img_bg} alt={topic} className="la-thumb-img" />
          )}
          <div className="la-hover-overlay">
            <div className="la-play-icon-circle">
              <img src={uiAssets.ic_play} alt="play" />
            </div>
          </div>
          <div
            className={`la-media-badge ${type === "audio" ? "is-audio" : "is-video"}`}
          >
            <img
              src={
                type === "audio"
                  ? uiAssets.ic_audio_bg
                  : uiAssets.ic_active_video
              }
              alt={type}
            />
            <span>{type.toUpperCase()}</span>
          </div>
        </div>

        <div className="la-body-content">
          <h4 className="la-title">{topic}</h4>
          {display_name && display_name !== topic && (
            <p className="la-subtitle">{display_name}</p>
          )}

          <div className="la-footer-actions">
            <button className="la-btn-play" onClick={() => setPopupMedia(data)}>
              <img src={uiAssets.ic_play} alt="play" />
              <span>Play Now</span>
            </button>
            <button
              className="la-btn-visibility"
              onClick={() => toggleHideAsset(asset_id, hidden)}
              title={hidden ? "UnHide" : "Hide"}
            >
              <img
                src={hidden ? uiAssets.ic_closed_eye : uiAssets.ic_eye}
                alt="visibility"
              />
            </button>
          </div>
        </div>
      </section>
    );
  };

  const FilterBar = () => {
    return (
      <section className="filter-card">
        {/* Row 1: Type tabs + dropdowns */}
        <section className="filter-row">
          <section className="filter-group">
            <small className="label">Filter by Type:</small>
            {[
              { key: "all", label: "All" },
              { key: "video", label: "Video", icon: uiAssets.ic_video },
              { key: "audio", label: "Audio", icon: uiAssets.ic_audio },
            ].map(({ key, label, icon }) => (
              <button
                key={key}
                className={`filter-btn ${filters.type === key ? "active" : ""}`}
                onClick={() => setFilters((prev) => ({ ...prev, type: key }))}
              >
                {icon && <img src={icon} alt={label} />}
                {label}
              </button>
            ))}
          </section>

          <section className="filter-dropdowns">
            {/* All Status */}
            <CustomSelect
              value={filters.status}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, status: val }))
              }
              options={STATUS}
              defaultLabel="All Status"
              isObjectOption={true}
            />
            <CustomSelect
              value={filters.language}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, language: val }))
              }
              options={LANGUAGES}
              defaultLabel=" Language"
              isObjectOption={true}
              showDefaultOption={false}
            />
            {/* All Medium */}
            {/* <CustomSelect
              value={filters.medium}
              onChange={(val) => {
                setSelectedMedium(val);
                setFilters((prev) => ({ ...prev, medium: val }));
              }}
              options={mediums}
              defaultLabel="All Medium"
              isObjectOption={false}
            /> */}
          </section>
        </section>

        {/* Row 2: Date filters + Show Hidden */}
        <section className="filter-row filter-row-date">
          <section className="filter-group">
            <small className="label">Filter by Date:</small>
            <button
              className={`filter-btn ${filters.dateMode === "specific" ? "active" : ""}`}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  dateMode: "specific",
                  from_date: "",
                  to_date: "",
                }))
              }
            >
              Specific Date
            </button>
            <button
              className={`filter-btn ${filters.dateMode === "range" ? "active" : ""}`}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  dateMode: "range",
                  on_date: "",
                }))
              }
            >
              Date Range
            </button>
          </section>

          {filters.dateMode === "specific" ? (
            <section className="filter-group">
              <input
                type="date"
                className="filter-date"
                value={filters.on_date}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, on_date: e.target.value }))
                }
              />
            </section>
          ) : (
            <section className="filter-group">
              <input
                type="date"
                className="filter-date"
                value={filters.from_date}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, from_date: e.target.value }))
                }
              />
              <small className="label">to</small>
              <input
                type="date"
                className="filter-date"
                value={filters.to_date}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, to_date: e.target.value }))
                }
              />
            </section>
          )}

          <section className="filter-group filter-hidden-toggle">
            <small className="label">Show Hidden:</small>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={filters.include_hidden}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    include_hidden: e.target.checked,
                  }))
                }
              />
              <span className="toggle-slider" />
            </label>
          </section>
        </section>
      </section>
    );
  };

  return (
    <section className="learning-assets">
      <div className="la-banner-dynamic">
        <div className="la-banner-content">
          <span className="la-banner-badge">AI-Powered Learning</span>
          <h2 className="la-banner-title">Your Learning Assets</h2>
          <p className="la-banner-desc">
            Explore and study your personalized AI-generated lessons, video
            content, and audio modules. Manage your library and track your
            progress in one place.
          </p>
          <div className="la-banner-features">
            <div className="feature-item">
              <span className="check-icon">✓</span>
              <span>Smart Content Selection</span>
            </div>
            <div className="feature-item">
              <span className="check-icon">✓</span>
              <span>Multi-Language Support</span>
            </div>
            <div className="feature-item">
              <span className="check-icon">✓</span>
              <span>Interactive Learning</span>
            </div>
          </div>
        </div>
        <button
          className="la-banner-btn"
          onClick={() => navigate(ROUTE_AI_GENERATE)}
        >
          + Generate New Asset
        </button>
      </div>

      <FilterBar />

      <div className="la-main-container">
        <div className="la-content">
          {Object.entries(groupedData).map(([subject, assets]) => {
            const videoAssets = assets.filter((a) => a.type === "video");
            const audioAssets = assets.filter((a) => a.type === "audio");

            return (
              <div key={subject} className="subject-section">
                {subject !== "__root__" && (
                  <div className="subject-header">
                    <span className="subject-icon">📚</span>
                    <h3>{subject}</h3>
                  </div>
                )}

                {videoAssets.length > 0 && (
                  <div className="media-type-section video-section">
                    <div className="media-type-header">
                      <h4>Video Lessons</h4>
                      <span className="media-count">
                        {videoAssets.length} Videos
                      </span>
                    </div>
                    <section className="assets-grid">
                      {videoAssets.map((asset) => (
                        <AssetCard key={asset.asset_id} data={asset} />
                      ))}
                    </section>
                  </div>
                )}

                {audioAssets.length > 0 && (
                  <div className="media-type-section audio-section">
                    <div className="media-type-header">
                      <h4>Audio Lessons</h4>
                      <span className="media-count">
                        {audioAssets.length} Audio
                      </span>
                    </div>
                    <section className="assets-grid">
                      {audioAssets.map((asset) => (
                        <AssetCard key={asset.asset_id} data={asset} />
                      ))}
                    </section>
                  </div>
                )}
              </div>
            );
          })}
          {apiAssets?.length === 0 && (
            <div className="no-assets-state">
              {/* <img src={uiAssets.ic_empty_assets} alt="No assets" /> */}
              <p>
                No learning assets found. Start by generating your first AI
                lesson!
              </p>
            </div>
          )}
        </div>
      </div>
      {popupMedia && (
        <MediaPopup
          data={popupMedia}
          onClose={() => {
            setPopupMedia(null);
            setShowAskBox(false);
          }}
          audioRef={audioRef}
          videoRef={videoRef}
          showAskBox={showAskBox}
          setShowAskBox={setShowAskBox}
          question={question}
          setQuestion={setQuestion}
          inputRef={inputRef}
          audioRes={audioRes}
          videoRes={videoRes}
          aiAudioChat={aiAudioChat}
          aiVideoChat={aiVideoChat}
          isListening={isListening}
          handleMicClick={handleMicClick}
          role={role}
        />
      )}
    </section>
  );
};
